import type { LevelStreamer } from "../level/LevelStreamer";
import type { HUD } from "../ui/HUD";

// One-line FX: tracks whether the segment under the player is a
// mirror-trick segment, and pushes that boolean to the HUD chip.
// MorphHexGame reads .inverted in its rotate handlers to flip the
// rotation direction before calling rotator.rotate(). The flag flips
// on/off automatically at the segment boundary because the boundary
// detection is driven by getSegmentAtPlayer (same source as
// TileHighlight, so they agree on which segment is "active").
export class MirrorTrickFx {
  // Public so MorphHexGame can read directly without a getter call;
  // we update it once per frame inside update().
  inverted = false;

  private streamer: LevelStreamer;
  private hud: HUD;
  // External override (e.g. ReverseGateFx during the gravity-flip
  // boss). When set, .inverted is forced regardless of the active
  // segment. null = no override (default).
  private forcedInvert: boolean | null = null;

  constructor(streamer: LevelStreamer, hud: HUD) {
    this.streamer = streamer;
    this.hud = hud;
  }

  update(): void {
    let next: boolean;
    if (this.forcedInvert !== null) {
      next = this.forcedInvert;
    } else {
      const seg = this.streamer.getSegmentAtPlayer();
      next = seg !== null && seg.trick === "mirror";
    }
    if (next !== this.inverted) {
      this.inverted = next;
      this.hud.setMirrorIndicator(next);
    }
  }

  // ReverseGateFx flips this true on bossStart and back to null on
  // bossCleared. While set, the per-segment mirror-trick check is
  // bypassed so the input feels reversed for the entire boss bar
  // even on segments that aren't mirror-trick themselves.
  setForcedInvert(value: boolean | null): void {
    this.forcedInvert = value;
  }

  // External reset (state transitions). Forces the HUD chip off so
  // it doesn't carry over visually into the next run.
  clear(): void {
    this.forcedInvert = null;
    if (this.inverted) {
      this.inverted = false;
      this.hud.setMirrorIndicator(false);
    }
  }
}
